import { FiChevronRight } from "react-icons/fi";

const SEGMENTS = [
  {
    label: "Application\nObservability",
    angle: -90, // top
    icon: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
        <path d="M7 8h2v5H7zM11 10h2v3h-2zM15 6h2v7h-2z" />
      </>
    ),
  },
  {
    label: "Infrastructure\nObservability",
    angle: -45,
    icon: (
      <>
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <circle cx="6" cy="6" r="1.2" fill="currentColor" stroke="none" />
        <circle cx="10" cy="6" r="1.2" fill="currentColor" stroke="none" />
        <circle cx="6" cy="18" r="1.2" fill="currentColor" stroke="none" />
        <circle cx="10" cy="18" r="1.2" fill="currentColor" stroke="none" />
      </>
    ),
  },
  {
    label: "Digital\nExperience",
    angle: 0,
    icon: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
        <circle cx="12" cy="10" r="3" />
        <path d="M12 7V5M12 13v2M9 10H7M15 10h2" />
      </>
    ),
  },
  {
    label: "Cloud\nMonitoring",
    angle: 45,
    icon: (
      <>
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
        <polyline points="12 12 12 8 10 10" />
        <line x1="12" y1="8" x2="14" y2="10" />
      </>
    ),
  },
  {
    label: "Log\nAnalytics",
    angle: 90, // bottom
    icon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </>
    ),
  },
  {
    label: "Kubernetes\nMonitoring",
    angle: 135,
    icon: (
      <>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  },
  {
    label: "Messaging Queue\nMonitoring",
    angle: 180, // left
    icon: (
      <>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <line x1="8" y1="10" x2="16" y2="10" />
        <line x1="8" y1="14" x2="13" y2="14" />
      </>
    ),
  },
  {
    label: "Active Alerts\nMechanism",
    angle: -135,
    icon: (
      <>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        <circle
          cx="18"
          cy="6"
          r="4"
          fill="rgba(255,180,80,0.25)"
          stroke="rgba(255,200,120,0.9)"
          strokeWidth="1.5"
        />
        <line x1="18" y1="4" x2="18" y2="6.5" />
        <line x1="18" y1="7.5" x2="18" y2="8" />
      </>
    ),
  },
];

// Precompute the connector line / dot positions for each angle
function orbitPoint(angle, r) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: 500 + r * Math.cos(rad),
    y: 500 + r * Math.sin(rad),
  };
}

// Descriptions map 1:1 to SEGMENTS by index, so icon + copy always stay in sync
const CAPABILITIES = [
  "Provides end-to-end visibility into application performance, availability, errors, transactions, and dependencies to quickly identify and resolve issues.",
  "Monitors servers, hosts, CPU, memory, disk, network, and system health to ensure infrastructure reliability and performance.",
  "Tracks real-user experience across web and mobile applications, including page performance, user journeys, errors, and overall experience.",
  "Provides visibility into cloud resources, services, workloads, and performance across platforms such as AWS, Azure, and GCP.",
  "Centralizes and analyzes application, infrastructure, and system logs to identify errors, investigate incidents, and derive actionable insights.",
  "Monitors Kubernetes clusters, nodes, pods, containers, workloads, and resource utilization to ensure containerized applications remain healthy.",
  "Monitors message queues, producers, consumers, message rates, backlog, failures, and processing latency to ensure reliable message delivery.",
  "Provides real-time notifications for critical events, threshold breaches, and service disruptions, enabling proactive incident detection and faster resolution.",
];

export function renderPlatformCapabilities() {
  return (
    <div className="transition-opacity duration-300" id="overview">

      <div className=" pb-8 mt-6 mb-6 text-center">
        <h1 className="text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight mb-6">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
            Netraa APM Platform
          </span>
        </h1>
        <p className="text-xl text-text-muted dark:text-gray-400 leading-relaxed max-w-7xl mx-auto">
          Cut through the noise of complex, distributed systems with unified telemetry and real-time context — all in one intelligent observability platform, built for teams who can't afford to guess.
        </p>
      </div>

      {/* Radial diagram */}
      <section className="netraa-diagram-section mb-20">
        <style>{`
          .netraa-diagram-section {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            font-family: 'Inter', sans-serif;

            /* Light mode (default) — solid orange badges for real contrast */
            --netraa-rays: rgba(234,88,12,0.2);
            --netraa-ambient-disc: rgba(234,88,12,0.16);
            --netraa-outer-dash-ring: rgba(234,88,12,0.3);
            --netraa-halo-mid: rgba(251,146,60,0.24);
            --netraa-halo-end: rgba(234,88,12,0.32);
            --netraa-halo-ring: rgba(234,88,12,0.5);
            --netraa-inner-ring: rgba(234,88,12,0.32);
            --netraa-orbit-track: rgba(234,88,12,0.5);
            --netraa-connector-main: #ea580c;
            --netraa-connector-dash: rgba(234,88,12,0.5);
            --netraa-dot: #c2410c;

            --netraa-icon-bg-a: #fb923c;
            --netraa-icon-bg-b: #ea580c;
            --netraa-icon-border: rgba(154,52,18,0.4);
            --netraa-icon-color: #ffffff;
            --netraa-icon-shadow: rgba(234,88,12,0.45);
            --netraa-icon-elevation: rgba(154,52,18,0.35);

            --netraa-icon-hover-bg-a: #f97316;
            --netraa-icon-hover-bg-b: #c2410c;
            --netraa-icon-hover-border: rgba(124,45,18,0.55);
            --netraa-icon-hover-color: #ffffff;
            --netraa-icon-hover-shadow: rgba(234,88,12,0.6);
            --netraa-icon-hover-shadow2: rgba(154,52,18,0.4);

            --netraa-label-color: #ea580c;
            --netraa-label-hover-color: #c2410c;
            --netraa-label-hover-shadow: rgba(234,88,12,0.5);

            --netraa-core-bg-a: #fff7ed;
            --netraa-core-bg-b: #fed7aa;
            --netraa-core-border: rgba(234,88,12,0.65);
            --netraa-core-shadow1: rgba(234,88,12,0.4);
            --netraa-core-shadow2: rgba(234,88,12,0.22);
            --netraa-core-shadow-inset: rgba(255,255,255,0.9);

            --netraa-core-title-color: #ea580c;
            --netraa-core-title-shadow: rgba(234,88,12,0.35);
            --netraa-core-subtitle-color: #f97316;
            --netraa-core-tagline-color: #c2410c;

            --netraa-footer-color: rgba(234,88,12,0.5);

            --netraa-cube-shadow1: rgba(234,88,12,0.6);
            --netraa-cube-shadow2: rgba(234,88,12,0.35);
          }

          /* Dark mode — unchanged, original warm-glow palette */
          .dark .netraa-diagram-section {
            --netraa-rays: rgba(255,255,255,0.055);
            --netraa-ambient-disc: rgba(255,160,50,0.22);
            --netraa-outer-dash-ring: rgba(255,200,100,0.07);
            --netraa-halo-mid: rgba(255,140,40,0.11);
            --netraa-halo-end: rgba(255,100,20,0.28);
            --netraa-halo-ring: rgba(255,170,70,0.32);
            --netraa-inner-ring: rgba(255,200,100,0.18);
            --netraa-orbit-track: rgba(255,170,80,0.22);
            --netraa-connector-main: rgba(255,200,100,0.48);
            --netraa-connector-dash: rgba(255,180,80,0.2);
            --netraa-dot: rgba(255,180,80,0.7);

            --netraa-icon-bg-a: rgba(255,255,255,.1);
            --netraa-icon-bg-b: rgba(255,120,30,.07);
            --netraa-icon-border: rgba(255,200,100,.25);
            --netraa-icon-color: rgba(255,220,180,.85);
            --netraa-icon-shadow: rgba(255,160,50,.15);
            --netraa-icon-elevation: transparent;

            --netraa-icon-hover-bg-a: rgba(255,220,140,.26);
            --netraa-icon-hover-bg-b: rgba(255,140,40,.16);
            --netraa-icon-hover-border: rgba(255,220,140,.65);
            --netraa-icon-hover-color: rgba(255,240,200,1);
            --netraa-icon-hover-shadow: rgba(255,200,80,.55);
            --netraa-icon-hover-shadow2: rgba(255,140,30,.2);

            --netraa-label-color: rgba(255,220,180,.8);
            --netraa-label-hover-color: rgba(255,240,210,1);
            --netraa-label-hover-shadow: rgba(255,200,100,.6);

            --netraa-core-bg-a: rgba(255,255,255,.12);
            --netraa-core-bg-b: rgba(255,120,30,.08);
            --netraa-core-border: rgba(255,200,120,.35);
            --netraa-core-shadow1: rgba(255,140,30,.3);
            --netraa-core-shadow2: rgba(255,100,20,.14);
            --netraa-core-shadow-inset: rgba(255,255,255,.2);

            --netraa-core-title-color: rgba(255,255,255,.95);
            --netraa-core-title-shadow: rgba(255,180,80,.7);
            --netraa-core-subtitle-color: rgba(255,220,160,.85);
            --netraa-core-tagline-color: rgba(255,200,140,.6);

            --netraa-footer-color: rgba(255,200,120,.28);

            --netraa-cube-shadow1: rgba(255,180,80,.8);
            --netraa-cube-shadow2: rgba(255,120,40,.4);
          }

          @keyframes netraa-rotate-slow {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes netraa-pulse-halo {
            0%, 100% { opacity: 0.35; transform: scale(1); }
            50%       { opacity: 0.55; transform: scale(1.04); }
          }

          .netraa-diagram-section .outer-ring {
            animation: netraa-rotate-slow 120s linear infinite;
            transform-origin: center center;
          }

          .netraa-diagram-section .composition {
            position: relative;
            width: 720px;
            max-width: 96vw;
            aspect-ratio: 1 / 1;
            flex-shrink: 0;
          }

          .netraa-diagram-section .svg-layer {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: visible;
          }

          .netraa-diagram-section .cards-wrapper {
            position: absolute;
            inset: 0;
          }

          .netraa-diagram-section .segment-card {
            position: absolute;
            width: 96px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            cursor: pointer;
          }

          .netraa-diagram-section .segment-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            animation: netraa-rotate-slow 120s linear infinite reverse;
          }

          .netraa-diagram-section .icon-circle {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: linear-gradient(145deg, var(--netraa-icon-bg-a), var(--netraa-icon-bg-b));
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1.5px solid var(--netraa-icon-border);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--netraa-icon-color);
            box-shadow: 0 0 10px var(--netraa-icon-shadow), 0 2px 8px var(--netraa-icon-elevation);
            transition: all .3s ease;
          }
          .netraa-diagram-section .icon-circle svg {
            width: 22px;
            height: 22px;
            stroke: currentColor;
            fill: none;
            stroke-width: 1.5;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
          .netraa-diagram-section .segment-card:hover .icon-circle {
            background: linear-gradient(145deg, var(--netraa-icon-hover-bg-a), var(--netraa-icon-hover-bg-b));
            border-color: var(--netraa-icon-hover-border);
            color: var(--netraa-icon-hover-color);
            box-shadow: 0 0 22px var(--netraa-icon-hover-shadow), 0 0 44px var(--netraa-icon-hover-shadow2);
            transform: scale(1.13);
          }

          .netraa-diagram-section .segment-label {
            font-family: 'Inter', sans-serif;
            font-weight: 500;
            font-size: 8.5px;
            letter-spacing: 0.055em;
            text-transform: uppercase;
            text-align: center;
            line-height: 1.4;
            color: var(--netraa-label-color);
            white-space: pre-line;
            max-width: 90px;
            transition: color .3s, text-shadow .3s;
          }
          .netraa-diagram-section .segment-card:hover .segment-label {
            color: var(--netraa-label-hover-color);
            text-shadow: 0 0 12px var(--netraa-label-hover-shadow);
          }

          .netraa-diagram-section .core {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 220px;
            height: 220px;
            border-radius: 50%;
            background: linear-gradient(145deg, var(--netraa-core-bg-a), var(--netraa-core-bg-b));
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1.5px solid var(--netraa-core-border);
            box-shadow:
              0 0 56px var(--netraa-core-shadow1),
              0 0 110px var(--netraa-core-shadow2),
              inset 0 1px 1px var(--netraa-core-shadow-inset);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            z-index: 20;
            pointer-events: none;
          }

          .netraa-diagram-section .cube-wrapper {
            margin-bottom: 6px;
            filter:
              drop-shadow(0 0 12px var(--netraa-cube-shadow1))
              drop-shadow(0 0 24px var(--netraa-cube-shadow2));
          }

          .netraa-diagram-section .core-title {
            font-family: 'Orbitron', sans-serif;
            font-weight: 700;
            font-size: 11px;
            letter-spacing: 0.15em;
            color: var(--netraa-core-title-color);
            text-transform: uppercase;
            line-height: 1.3;
            text-shadow: 0 0 16px var(--netraa-core-title-shadow);
          }
          .netraa-diagram-section .core-subtitle {
            font-family: 'Orbitron', sans-serif;
            font-weight: 400;
            font-size: 9px;
            letter-spacing: 0.1em;
            color: var(--netraa-core-subtitle-color);
          }
          .netraa-diagram-section .core-tagline {
            font-family: 'Inter', sans-serif;
            font-weight: 300;
            font-size: 8px;
            letter-spacing: 0.08em;
            color: var(--netraa-core-tagline-color);
            margin-top: 5px;
            text-transform: uppercase;
          }

          .netraa-diagram-section .footer-label {
            position: absolute;
            bottom: 8px;
            left: 50%;
            transform: translateX(-50%);
            font-family: 'Orbitron', sans-serif;
            font-size: 7.5px;
            letter-spacing: 0.25em;
            color: var(--netraa-footer-color);
            text-transform: uppercase;
            white-space: nowrap;
            pointer-events: none;
          }

          .netraa-diagram-section .halo-pulse {
            animation: netraa-pulse-halo 3.5s ease-in-out infinite;
            transform-origin: 500px 500px;
          }

          @media (max-width: 800px) {
            .netraa-diagram-section .composition {
              width: min(96vw, 96vh);
            }
          }
        `}</style>

        <div className="composition" role="img" aria-label="NETRAA AI Platform radial infographic">
          <svg className="svg-layer" viewBox="0 0 1000 1000" aria-hidden="true">
            <defs>
              <radialGradient id="netraa-rg-bg" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--netraa-ambient-disc)" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
              <radialGradient id="netraa-rg-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="transparent" />
                <stop offset="68%" stopColor="var(--netraa-halo-mid)" />
                <stop offset="100%" stopColor="var(--netraa-halo-end)" />
              </radialGradient>
              <filter id="netraa-f-softglow">
                <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <g stroke="var(--netraa-rays)">
              <line x1="500" y1="380" x2="500" y2="10" strokeWidth="1" />
              <line x1="585" y1="406" x2="836" y2="155" strokeWidth="1" />
              <line x1="620" y1="500" x2="990" y2="500" strokeWidth="1" />
              <line x1="585" y1="594" x2="836" y2="845" strokeWidth="1" />
              <line x1="500" y1="620" x2="500" y2="990" strokeWidth="1" />
              <line x1="415" y1="594" x2="164" y2="845" strokeWidth="1" />
              <line x1="380" y1="500" x2="10" y2="500" strokeWidth="1" />
              <line x1="415" y1="406" x2="164" y2="155" strokeWidth="1" />
              <line x1="543" y1="388" x2="668" y2="83" strokeWidth="1" />
              <line x1="608" y1="452" x2="913" y2="327" strokeWidth="1" />
              <line x1="608" y1="548" x2="913" y2="673" strokeWidth="1" />
              <line x1="543" y1="612" x2="668" y2="917" strokeWidth="1" />
              <line x1="457" y1="612" x2="332" y2="917" strokeWidth="1" />
              <line x1="392" y1="548" x2="87" y2="673" strokeWidth="1" />
              <line x1="392" y1="452" x2="87" y2="327" strokeWidth="1" />
              <line x1="457" y1="388" x2="332" y2="83" strokeWidth="1" />
            </g>

            <circle cx="500" cy="500" r="340" fill="url(#netraa-rg-bg)" />
            <circle
              cx="500"
              cy="500"
              r="428"
              fill="none"
              stroke="var(--netraa-outer-dash-ring)"
              strokeWidth="1"
              strokeDasharray="4 9"
            />
            <circle
              cx="500"
              cy="500"
              r="156"
              fill="url(#netraa-rg-halo)"
              className="halo-pulse"
              filter="url(#netraa-f-softglow)"
            />
            <circle
              cx="500"
              cy="500"
              r="156"
              fill="none"
              stroke="var(--netraa-halo-ring)"
              strokeWidth="2"
              className="halo-pulse"
              filter="url(#netraa-f-softglow)"
            />
            <circle cx="500" cy="500" r="124" fill="none" stroke="var(--netraa-inner-ring)" strokeWidth="1" />
          </svg>

          <svg className="svg-layer outer-ring" viewBox="0 0 1000 1000" aria-hidden="true">
            <defs>
              <filter id="netraa-f-glow-r">
                <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="netraa-f-dotglow-r">
                <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <circle
              cx="500"
              cy="500"
              r="290"
              fill="none"
              stroke="var(--netraa-orbit-track)"
              strokeWidth="1.5"
              filter="url(#netraa-f-glow-r)"
            />

            {SEGMENTS.map((seg, i) => {
              const outer = orbitPoint(seg.angle, 290);
              const dot = orbitPoint(seg.angle, 290);
              const innerStart = orbitPoint(seg.angle, 108);
              const innerEnd = orbitPoint(seg.angle, 152);
              return (
                <g key={i}>
                  <line
                    x1={orbitPoint(seg.angle, 266).x}
                    y1={orbitPoint(seg.angle, 266).y}
                    x2={outer.x}
                    y2={outer.y}
                    stroke="var(--netraa-connector-main)"
                    strokeWidth="1.5"
                    filter="url(#netraa-f-glow-r)"
                  />
                  <line
                    x1={innerStart.x}
                    y1={innerStart.y}
                    x2={innerEnd.x}
                    y2={innerEnd.y}
                    stroke="var(--netraa-connector-dash)"
                    strokeWidth="1"
                    strokeDasharray="3 6"
                  />
                  <circle cx={dot.x} cy={dot.y} r="5" fill="var(--netraa-dot)" filter="url(#netraa-f-dotglow-r)" />
                </g>
              );
            })}
          </svg>

          <div className="cards-wrapper outer-ring" aria-hidden="true">
            {SEGMENTS.map((seg, i) => {
              const rad = (seg.angle * Math.PI) / 180;
              const x = Math.round(266 * Math.cos(rad));
              const y = Math.round(266 * Math.sin(rad));
              return (
                <div
                  key={i}
                  className="segment-card"
                  style={{
                    left: "50%",
                    top: "50%",
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  }}
                >
                  <div className="segment-inner">
                    <div className="icon-circle">
                      <svg viewBox="0 0 24 24">{seg.icon}</svg>
                    </div>
                    <div className="segment-label">{seg.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="core">
            <div className="cube-wrapper">
              <svg viewBox="0 0 64 64" width="58" height="58" fill="none" aria-label="AI core cube">
                <defs>
                  <linearGradient id="netraa-ct" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffb347" stopOpacity="0.6" />
                  </linearGradient>
                  <linearGradient id="netraa-cl" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ff6b35" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#c0392b" stopOpacity="0.5" />
                  </linearGradient>
                  <linearGradient id="netraa-cr" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ffcba4" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#ff6b35" stopOpacity="0.4" />
                  </linearGradient>
                </defs>
                <polygon points="32,6 54,18 32,30 10,18" fill="url(#netraa-ct)" />
                <polygon points="10,18 32,30 32,54 10,42" fill="url(#netraa-cl)" />
                <polygon points="54,18 54,42 32,54 32,30" fill="url(#netraa-cr)" />
                <polygon points="32,6 54,18 32,30 10,18" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.8" />
                <polygon points="10,18 32,30 32,54 10,42" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />
                <polygon points="54,18 54,42 32,54 32,30" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />
                <circle cx="32" cy="30" r="3" fill="white" opacity="0.9" />
                <circle cx="32" cy="30" r="7" fill="white" opacity="0.12" />
              </svg>
            </div>
            <div className="core-title">
              NETRAA APM
              <br />
              <span className="core-subtitle">AI PLATFORM</span>
            </div>
            <div className="core-tagline">Unified Intelligence Layer</div>
          </div>

          <div className="footer-label" aria-hidden="true">
            Powered by NETRAA Intelligence Engine
          </div>
        </div>
      </section>

      {/* Capability breakdown — same 8 icons, in reading order */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        {SEGMENTS.map((seg, i) => (
          <div key={i} className="flex gap-4 pl-5 border-l-2 border-orange-200 dark:border-orange-500/20">
            <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-sm">
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                {seg.icon}
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-text-main dark:text-white mb-1.5">
                {seg.label.replace("\n", " ")}
              </h3>
              <p className="text-sm text-text-muted dark:text-gray-400 leading-relaxed">
                {CAPABILITIES[i]}
              </p>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}